import { useEffect, useMemo } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Star, Zap, Heart, Gift } from "lucide-react";
import alixDevice from "@/assets/alix-secret-twin-haarentfernung-geraet.png";
import lifestyleImg from "@/assets/haarentfernung-glatte-arme.webp";
import laserBehandlung1 from "@/assets/diodenlaser-haarentfernung-beine-nuernberg.webp";
import laserBehandlung2 from "@/assets/laser-haarentfernung-behandlung-nuernberg.webp";
import laserBehandlung3 from "@/assets/dauerhafte-haarentfernung-alix-laser-nuernberg.webp";
import smoothImg from "@/assets/haarentfernung-glatte-haut-koerper.webp";
import SEO from "@/components/SEO";

const PLANITY_URL = "https://www.planity.com/de-DE/22-studios-90469-nurnberg";


const einzelpreise = [
  { zone: "Oberlippe", preis: "29,00" },
  { zone: "Kinn", preis: "29,00" },
  { zone: "Wangen", preis: "29,00" },
  { zone: "Hals", preis: "39,00" },
  { zone: "Nase", preis: "29,00" },
  { zone: "Nacken", preis: "29,00" },
  { zone: "Gesicht komplett (inkl. Kinn, Oberlippe, Wangen)", preis: "79,00" },
  { zone: "Achseln", preis: "39,00" },
  { zone: "Unterarme", preis: "49,00" },
  { zone: "Oberarme", preis: "59,00" },
  { zone: "Brust komplett", preis: "49,00" },
  { zone: "Rücken unten / Steiß", preis: "39,00" },
  { zone: "Rücken komplett", preis: "89,00" },
  { zone: "Bauch komplett", preis: "59,00" },
  { zone: "Bikini Zone", preis: "39,00" },
  { zone: "Intim komplett (Bikinizone, Intim, Pofalte)", preis: "109,00" },
  { zone: "Unterschenkel", preis: "69,00" },
  { zone: "Oberschenkel", preis: "69,00" },
  { zone: "Beine komplett (inkl. Oberschenkel, Unterschenkel, Füße, Zeh)", preis: "159,00" },
] as const;

const pakete = [
  { name: "Paket 1 – Ganzkörper", preis: "369,00" },
  { name: "Paket 2 – Ganzkörper (inkl. Gesicht)", preis: "389,00" },
  { name: "Paket 3 – Unterschenkel & Intim", preis: "189,00" },
  { name: "Paket 4 – Achseln & Intim", preis: "149,00" },
  { name: "Paket 5 – Achseln, Unterschenkel & Intim", preis: "229,00" },
  { name: "Paket 6 – Achseln, Unterschenkel & Bikini", preis: "159,00" },
  { name: "Paket 7 – Achseln, Brust, Bauch, Rücken komplett", preis: "219,00" },
] as const;

const feelings = [
  { icon: Heart, title: "Samtweiche Haut", desc: "Spüre den Unterschied nach jeder Sitzung – glatte, gepflegte Haut ohne Stoppeln." },
  { icon: Sparkles, title: "Neue Freiheit", desc: "Kein Rasieren, kein Wachsen, kein Stress mehr. Einfach wohlfühlen – jeden Tag." },
  { icon: Star, title: "Selbstbewusstsein", desc: "Zeig dich, wie du willst – zu jeder Jahreszeit, ohne Kompromisse." },
] as const;

const ablaufSchritte = [
  { step: "01", title: "Beratung", desc: "Wir analysieren deinen Hauttyp und erstellen deinen persönlichen Behandlungsplan." },
  { step: "02", title: "Vorbereitung", desc: "Die KI erkennt deinen Hauttyp automatisch – keine komplizierten Einstellungen nötig." },
  { step: "03", title: "Behandlung", desc: "Angenehm dank –26°C Kühlung. Lehne dich zurück und entspanne." },
  { step: "04", title: "Ergebnis", desc: "Bereits nach wenigen Sitzungen: sichtbar glattere, geschmeidigere Haut." },
] as const;

const techStats = [
  { label: "Kühlung", value: "–26°C" },
  { label: "Leistung", value: "3.000W" },
  { label: "Wellenlängen", value: "4" },
  { label: "Steuerung", value: "Voll-KI" },
] as const;

const openPlanity = () => window.open(PLANITY_URL, '_blank', 'noopener,noreferrer');

const HaarentfernungPage = () => {
  const isMobile = useIsMobile();
  const pop = isMobile ? "" : "hover-pop";

  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Dauerhafte Haarentfernung mit Laser Nürnberg",
    "alternateName": ["Laser Haarentfernung Nürnberg", "Laserbehandlung Haarentfernung Nürnberg"],
    "provider": {
      "@type": "BeautySalon",
      "name": "22 Studios",
      "address": { "@type": "PostalAddress", "addressLocality": "Nürnberg", "addressRegion": "Bayern", "postalCode": "90469", "addressCountry": "DE" }
    },
    "areaServed": {
      "@type": "City",
      "name": "Nürnberg"
    },
    "description": "Dauerhafte Laser-Haarentfernung in Nürnberg mit dem ALIX Secret Twin. Schmerzarme Laserbehandlung für alle Hauttypen, KI-gesteuert, ab 29 €.",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "29.00",
      "highPrice": "389.00",
      "priceCurrency": "EUR"
    }
  }), []);

  return (
    <main className="min-h-screen bg-background relative">
      <SEO
        title="Haarentfernung Nürnberg – Diodenlaser | 22 Studios"
        description="Dauerhafte Haarentfernung mit Diodenlaser in Nürnberg – schmerzarm, für alle Hauttypen, KI-gesteuert. Ab 29 € pro Sitzung. 6 buchen, 7. & 8. gratis!"
        canonical="https://22studios.de/nuernberg/haarentfernung"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <AnimatedBackground />
      <Navigation />

      {/* Hero */}
      <section className="pt-24 md:pt-36 pb-8 md:pb-20 relative z-10" aria-label="Dauerhafte Laser-Haarentfernung bei 22 Studios Nürnberg">
        <div className="container px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
            <div className="space-y-4 md:space-y-6">
              <div className="flex items-center gap-3">
                <span className="text-sm tracking-wide uppercase text-label font-medium">
                  ✨ Jetzt neu
                </span>
                 <span className="text-xs font-medium tracking-wide uppercase px-3 py-1 rounded-full bg-highlight/10 text-highlight border border-highlight/20">
                  6 buchen – 7. & 8. gratis
                </span>
              </div>
              <span className="text-sm tracking-wide uppercase text-label font-medium block">
                Dauerhafte Haarentfernung
              </span>
              <h1 className="text-display md:text-display-lg font-serif font-light text-foreground leading-tight">
                Glatte Haut.<br />Ohne Kompromisse.
              </h1>
              <p className="text-sm md:text-base text-primary font-medium">
                Dauerhafte Laser-Haarentfernung in Nürnberg
              </p>
               <p className="text-base md:text-lg text-muted-foreground font-light leading-relaxed">
                 Dauerhafte Haarentfernung mit modernem Diodenlaser. Stell dir vor, du wachst auf und deine Haut ist einfach perfekt – glatt, weich,
                 gepflegt. Keine Rasur, kein Waxing, kein täglicher Aufwand. Nur du und dein
                 bestes Hautgefühl. Schmerzarme Diodenlaser-Behandlung – dauerhaft.
               </p>
              <ul className="flex flex-wrap gap-2 list-none p-0" aria-label="Vorteile der Behandlung">
                {["Schmerzarm", "Alle Hauttypen", "Dauerhaft", "KI-gesteuert", "Diodenlaser"].map((tag) => (
                  <li key={tag}>
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-secondary/50 text-muted-foreground border border-border/30">
                      {tag}
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-blush-dark px-8 py-6 text-base font-medium rounded-full hover-lift shadow-glow"
                onClick={openPlanity}
              >
                Jetzt Beratungstermin buchen
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-4 items-start">
              <div className={`rounded-2xl overflow-hidden shadow-soft-lg ${pop}`}>
                <img
                  src={laserBehandlung1}
                  alt="Dauerhafte Laser-Haarentfernung Ergebnis – glatte Haut im Beauty Studio Nürnberg"
                  className="w-full aspect-[4/5] object-cover"
                  width={400}
                  height={500}
                />
              </div>
              <div className={`rounded-2xl overflow-hidden shadow-soft-lg ${pop} mt-8`}>
                <img
                  src={laserBehandlung3}
                  alt="Samtweiche glatte Haut nach Laser-Haarentfernung Nürnberg"
                  className="w-full aspect-[4/5] object-cover"
                  width={400}
                  height={500}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emotional – Das Gefühl danach */}
      <section className="py-12 md:py-28 relative z-10" aria-label="Vorteile der dauerhaften Laser-Haarentfernung">
        <div className="container px-4 md:px-6">
          <div className="grid lg:grid-cols-5 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-2">
              <div className={`rounded-2xl overflow-hidden shadow-soft-lg ${pop}`}>
                <img
                  src={laserBehandlung2}
                  alt="Glatte Haut nach Laser-Haarentfernung bei 22 Studios Nürnberg"
                  className="w-full aspect-[3/4] object-cover"
                  width={600}
                  height={450}
                />
              </div>
            </div>
            <div className="lg:col-span-3 space-y-6 md:space-y-8">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <span className="w-12 h-px bg-primary" aria-hidden="true" />
                  <span className="text-sm tracking-wide uppercase text-label font-medium">Das Gefühl danach</span>
                </div>
                <h2 className="text-heading md:text-display font-serif font-light text-foreground mb-4">
                  Mehr als nur Laser-Haarentfernung
                </h2>
                <p className="text-muted-foreground font-light leading-relaxed">
                  Es geht nicht nur um glatte Haut – es geht um das Gefühl, sich in seiner
                  Haut rundum wohlzufühlen. Die Freiheit, spontan ins Schwimmbad zu gehen,
                  ohne nachzudenken. Das Selbstbewusstsein, sich jeden Tag schön zu fühlen.
                </p>
              </div>
              <div className="grid sm:grid-cols-3 gap-6">
                {feelings.map((f) => (
                  <article key={f.title} className="text-center space-y-3 group">
                    <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto group-hover:bg-primary/40 transition-colors" aria-hidden="true">
                      <f.icon className="w-6 h-6 text-foreground" />
                    </div>
                    <h3 className="font-serif font-medium text-lg">{f.title}</h3>
                    <p className="text-sm text-muted-foreground font-light leading-relaxed">{f.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="py-12 md:py-28 bg-secondary/30 relative z-10" aria-label="Behandlungsablauf">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <div className="flex items-center justify-center gap-4 mb-6" aria-hidden="true">
              <span className="w-12 h-px bg-primary" />
              <span className="text-sm tracking-wide uppercase text-label font-medium">Dein Weg</span>
              <span className="w-12 h-px bg-primary" />
            </div>
            <h2 className="text-heading md:text-display font-serif font-light text-foreground">
              So einfach geht's
            </h2>
          </div>

          <ol className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-5xl mx-auto list-none p-0">
            {ablaufSchritte.map((s) => (
              <li key={s.step} className="text-center space-y-4 group">
                <span className="text-4xl font-serif font-light text-primary" aria-hidden="true">{s.step}</span>
                <h3 className="text-lg font-serif font-medium">{s.title}</h3>
                <p className="text-sm text-muted-foreground font-light leading-relaxed">{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Device */}
      <section className="py-12 md:py-28 relative z-10 overflow-hidden" aria-label="ALIX Secret Twin Technologie">
        <div className="container px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="order-2 lg:order-1 space-y-4 md:space-y-6">
              <div className="flex items-center gap-4 mb-2">
                <span className="w-12 h-px bg-primary" aria-hidden="true" />
                <span className="text-sm tracking-wide uppercase text-label font-medium">Unsere Technologie</span>
              </div>
              <h2 className="text-heading md:text-display font-serif font-light text-foreground">
                ALIX Secret Twin – Dein Diodenlaser für dauerhafte Haarentfernung
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed">
                Modernste Diodenlaser- und KI-Technologie für maximalen Komfort und beste Ergebnisse.
                Das Gerät erkennt deinen Hauttyp automatisch und passt die Behandlung in Echtzeit an –
                für eine sichere, effektive Haarentfernung mit Diodenlaser bei jedem Hautton und jeder Haarstruktur.
              </p>
              <dl className="grid grid-cols-2 gap-3 md:gap-4 pt-3 md:pt-4">
                {techStats.map((stat) => (
                  <div key={stat.label} className={`bg-secondary/40 rounded-xl p-3 md:p-4 text-center ${pop}`}>
                    <dt className="text-xs text-muted-foreground order-2 mt-1">{stat.label}</dt>
                    <dd className="text-2xl font-serif font-medium text-foreground">{stat.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative max-w-sm">
                <div className="absolute -inset-8 bg-primary/10 rounded-full blur-3xl" aria-hidden="true" />
                <img
                  src={alixDevice}
                  alt="ALIX Secret Twin – Lasergerät für dauerhafte Haarentfernung mit 4 Wellenlängen und KI-Steuerung"
                  className={`relative w-full h-auto ${pop}`}
                  width={400}
                  height={500}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reminder Banner */}
      <section className="relative z-10 pb-4" aria-label="Aktions-Erinnerung">
        <div className="container px-4 md:px-6">
          <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 md:p-8 flex items-center gap-3 md:gap-4 justify-center">
            <Gift className="w-6 h-6 text-highlight flex-shrink-0" aria-hidden="true" />
             <p className="text-foreground font-serif text-lg md:text-xl font-light">
              Denk dran: <span className="font-medium">6+2 gratis</span> – die 7. & 8. Behandlung schenken wir dir.
            </p>
          </div>
        </div>
      </section>

      {/* Preise */}
      <section className="py-12 md:py-28 bg-secondary/30 relative z-10" aria-label="Preisliste Laser-Haarentfernung Nürnberg">
        <div className="container px-4 md:px-6">
          <header className="text-center mb-8 md:mb-16">
            <div className="flex items-center justify-center gap-4 mb-6" aria-hidden="true">
              <span className="w-12 h-px bg-primary" />
              <span className="text-sm tracking-wide uppercase text-label font-medium">Preise</span>
              <span className="w-12 h-px bg-primary" />
            </div>
            <h2 className="text-heading md:text-display font-serif font-light text-foreground">
              Preisliste Laser-Haarentfernung
            </h2>
            <p className="text-muted-foreground mt-4 font-light">Damen & Herren · Alle Preise pro Sitzung</p>
          </header>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 max-w-5xl mx-auto">
            {/* Einzelpreise */}
            <div className={`bg-background rounded-2xl p-5 md:p-8 shadow-soft ${pop} group`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors" aria-hidden="true">
                  <Zap className="w-5 h-5 text-foreground" />
                </div>
                <h3 className="text-xl font-serif font-medium">Einzelzonen</h3>
              </div>
              <ul className="space-y-1 list-none p-0">
                {einzelpreise.map((item, i) => (
                  <li key={i} className="flex justify-between items-center py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors last:border-b-0">
                    <span className="text-muted-foreground text-sm">{item.zone}</span>
                    <span className="font-medium whitespace-nowrap ml-4">{item.preis}&nbsp;€</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pakete */}
            <div className="space-y-8">
              <div className={`bg-background rounded-2xl p-5 md:p-8 shadow-soft ${pop} group`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors" aria-hidden="true">
                    <Star className="w-5 h-5 text-foreground" />
                  </div>
                  <h3 className="text-xl font-serif font-medium">Pakete</h3>
                </div>
                <ul className="space-y-1 list-none p-0">
                  {pakete.map((item, i) => (
                    <li key={i} className="flex justify-between items-center py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors last:border-b-0">
                      <span className="text-muted-foreground text-sm">{item.name}</span>
                      <span className="font-medium whitespace-nowrap ml-4">{item.preis}&nbsp;€</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={`rounded-2xl overflow-hidden shadow-soft-lg ${pop}`}>
                <img
                  src={lifestyleImg}
                  alt="Entspannte Atmosphäre bei der Laser-Haarentfernung im Premium Beauty Studio Nürnberg"
                  className="w-full aspect-[3/2] object-cover"
                  width={600}
                  height={400}
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="text-center mt-10 md:mt-16">
            <Button
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-blush-dark px-10 py-6 text-base font-medium rounded-full hover-lift shadow-glow"
              onClick={openPlanity}
            >
              Jetzt Termin buchen
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default HaarentfernungPage;
